import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  Typography,
  Container,
  Stack,
  Paper,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  LinearProgress,
} from '@mui/material';
import {
  Lock as LockIcon,
  Security as SecurityIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { apiUrl } from '../utils/api';
import { setDeviceToken } from '../utils/deviceToken';

interface SetupAccountProps {
  onLogin: (data: any, rememberMe: boolean) => void;
}

type Phase = 'loading' | 'invalid' | 'password' | 'mfa';

// Mirror of the backend password policy (password_validator.py) for instant
// feedback. The backend remains the source of truth.
const PASSWORD_RULES = [
  { label: 'At least 10 characters', test: (p: string) => p.length >= 10 },
  { label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { label: 'One number', test: (p: string) => /[0-9]/.test(p) },
  {
    label: 'One special character',
    test: (p: string) => /[!@#$%^&*()_+\-=[\]{}|;:,.<>?/~`'"\\]/.test(p),
  },
];

const SetupAccount: React.FC<SetupAccountProps> = ({ onLogin }) => {
  const [phase, setPhase] = useState<Phase>('loading');
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [mfaQrCode, setMfaQrCode] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Validate the token from the URL on mount.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = (params.get('token') || '').trim();
    setToken(t);

    if (!t) {
      setPhase('invalid');
      setError('This setup link is missing its token. Please use the link from your welcome email.');
      return;
    }

    (async () => {
      try {
        const res = await fetch(apiUrl(`/api/account/setup/validate?token=${encodeURIComponent(t)}`), {
          credentials: 'include',
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.valid) {
          setPhase('invalid');
          setError(data.error || 'This setup link is invalid or has expired.');
          return;
        }
        setEmail(data.email || '');
        setFirstName(data.first_name || '');
        setPhase('password');
      } catch {
        setPhase('invalid');
        setError('Cannot reach the server. Please check your connection and try again.');
      }
    })();
  }, []);

  const allRulesPass = PASSWORD_RULES.every((r) => r.test(password));
  const passwordsMatch = password.length > 0 && password === confirmPassword;

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!allRulesPass) {
      setError('Password does not meet all the requirements below.');
      return;
    }
    if (!passwordsMatch) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/account/setup'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.error || 'Could not set your password. Please try again.');
        return;
      }

      // Backend drops us into the same MFA-setup state the normal login uses.
      if (data.mfa_required && data.mfa_setup) {
        setMfaQrCode(data.qr_code);
        setMfaSecret(data.secret);
        setPhase('mfa');
      } else if (data.mfa_required) {
        setPhase('mfa');
      } else {
        // No MFA step (shouldn't happen) — treat as logged in.
        onLogin(data, true);
        window.location.replace('/');
      }
    } catch {
      setError('Cannot reach the server. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/mfa/verify-login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: mfaCode }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.error || 'Invalid code. Please try again.');
        return;
      }

      // The setup device is auto-trusted by the backend (email link already
      // verified ownership). Persist the returned trust token so this device
      // is remembered for 30 days.
      if (data.device_token) {
        setDeviceToken(data.device_token);
      }

      // Fully set up and verified — log in and go to the dashboard.
      onLogin(data, true);
      window.location.replace('/');
    } catch {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Paper
            elevation={0}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 80,
              height: 80,
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #2f4ae1 0%, #12b59f 100%)',
              mb: 2,
              boxShadow: '0 8px 32px rgba(47, 74, 225, 0.3)',
            }}
          >
            <LockIcon sx={{ fontSize: 44, color: 'white' }} />
          </Paper>
          <Typography variant="h4" fontWeight={700} sx={{ color: '#1f2937', mb: 1 }}>
            {phase === 'mfa' ? 'Set Up Authenticator' : 'Set Up Your Account'}
          </Typography>
          <Typography variant="body1" sx={{ color: '#6b7280' }}>
            {phase === 'mfa'
              ? 'Scan the QR code and enter the 6-digit code'
              : 'Welcome to PM System PRO'}
          </Typography>
        </Box>

        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            background: 'white',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Loading */}
            {phase === 'loading' && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2, color: '#6b7280' }}>Validating your link…</Typography>
              </Box>
            )}

            {/* Invalid / expired */}
            {phase === 'invalid' && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CancelIcon sx={{ fontSize: 56, color: '#ef4444', mb: 1 }} />
                <Typography variant="h6" sx={{ color: '#1f2937', mb: 1 }}>
                  Link not valid
                </Typography>
                <Typography sx={{ color: '#6b7280', mb: 3 }}>
                  {error || 'This setup link is invalid or has expired.'}
                </Typography>
                <Button variant="outlined" onClick={() => window.location.replace('/')}>
                  Go to Login
                </Button>
              </Box>
            )}

            {/* Set password */}
            {phase === 'password' && (
              <form onSubmit={handleSetPassword}>
                <Stack spacing={2.5}>
                  <Typography variant="body2" sx={{ color: '#4b5563', textAlign: 'center' }}>
                    {firstName ? `Hi ${firstName}, ` : ''}set a password for{' '}
                    <strong>{email}</strong>
                  </Typography>

                  <TextField
                    label="New Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    required
                    autoFocus
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="Confirm Password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    fullWidth
                    required
                    error={confirmPassword.length > 0 && !passwordsMatch}
                    helperText={
                      confirmPassword.length > 0 && !passwordsMatch ? 'Passwords do not match' : ' '
                    }
                  />

                  {/* Live policy checklist */}
                  <Box sx={{ bgcolor: '#f9fafb', borderRadius: 2, p: 2 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                      Password must contain:
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                      {PASSWORD_RULES.map((rule) => {
                        const ok = rule.test(password);
                        return (
                          <Box key={rule.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {ok ? (
                              <CheckCircleIcon sx={{ fontSize: 16, color: '#12b59f' }} />
                            ) : (
                              <CancelIcon sx={{ fontSize: 16, color: '#d1d5db' }} />
                            )}
                            <Typography
                              variant="caption"
                              sx={{ color: ok ? '#12b59f' : '#9ca3af' }}
                            >
                              {rule.label}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading || !allRulesPass || !passwordsMatch}
                    sx={{
                      py: 1.5,
                      background: 'linear-gradient(135deg, #2f4ae1 0%, #12b59f 100%)',
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Set Password & Continue'}
                  </Button>
                </Stack>
              </form>
            )}

            {/* MFA setup */}
            {phase === 'mfa' && (
              <form onSubmit={handleVerifyMfa}>
                <Stack spacing={2.5} alignItems="center">
                  {mfaQrCode && (
                    <>
                      <Typography variant="body2" sx={{ color: '#4b5563', textAlign: 'center' }}>
                        Scan this QR code with Microsoft or Google Authenticator:
                      </Typography>
                      <Box sx={{ p: 1.5, bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2 }}>
                        <img src={mfaQrCode} alt="MFA QR Code" style={{ width: 180, height: 180 }} />
                      </Box>
                      {mfaSecret && (
                        <Typography variant="caption" sx={{ color: '#6b7280', textAlign: 'center', wordBreak: 'break-all' }}>
                          Can't scan? Enter this key manually: <strong>{mfaSecret}</strong>
                        </Typography>
                      )}
                    </>
                  )}

                  <TextField
                    label="6-digit code"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    fullWidth
                    autoFocus
                    inputProps={{ inputMode: 'numeric', style: { letterSpacing: '8px', textAlign: 'center', fontSize: '1.4rem' } }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading || mfaCode.length !== 6}
                    sx={{
                      py: 1.5,
                      background: 'linear-gradient(135deg, #2f4ae1 0%, #12b59f 100%)',
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Sign In'}
                  </Button>
                </Stack>
              </form>
            )}

            {/* Footer badge */}
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 999,
                  bgcolor: 'rgba(47, 74, 225, 0.06)',
                  border: '1px solid rgba(47, 74, 225, 0.15)',
                }}
              >
                <SecurityIcon sx={{ fontSize: 14, color: '#2f4ae1' }} />
                <Typography variant="caption" sx={{ color: '#2f4ae1', fontWeight: 600, fontSize: '0.7rem' }}>
                  Secure account setup
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default SetupAccount;
